import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export interface ServiceCard {
  title: string;
  description: string;
  image: string;
  href: string;
  badge?: string;
  features?: string[];
  keyword?: string;
}

interface ServicesGridProps {
  eyebrow: string;
  headline: string;
  subheadline?: string;
  cards: ServiceCard[];
}

const ServicesGrid = ({ eyebrow, headline, subheadline, cards }: ServicesGridProps) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-white py-32" id="services">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          ref={ref}
          className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-10 h-px bg-amber-500" />
            <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">{eyebrow}</span>
            <div className="w-10 h-px bg-amber-500" />
          </div>
          <h2 className="font-serif text-5xl md:text-6xl text-zinc-900 leading-tight mb-5">
            {headline}
          </h2>
          {subheadline && (
            <p className="text-zinc-500 text-lg leading-relaxed">{subheadline}</p>
          )}
        </div>

        {/* Asymmetric grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Featured card — large */}
          {cards[0] && (
            <div
              className={`lg:col-span-7 group relative overflow-hidden rounded-3xl cursor-pointer transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ minHeight: '580px', transitionDelay: '0ms' }}
            >
              <img
                src={cards[0].image}
                alt={cards[0].title}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/95 via-zinc-950/40 to-zinc-950/5" />

              {cards[0].badge && (
                <div className="absolute top-6 left-6 bg-amber-500 text-zinc-950 text-xs font-bold px-4 py-1.5 rounded-full tracking-wide">
                  {cards[0].badge}
                </div>
              )}

              <div className="absolute bottom-0 left-0 right-0 p-9">
                {cards[0].keyword && (
                  <span className="text-amber-400/80 text-xs font-semibold tracking-[0.2em] uppercase mb-3 block">
                    {cards[0].keyword}
                  </span>
                )}
                <h3 className="font-serif text-3xl text-white font-semibold mb-3">{cards[0].title}</h3>
                <p className="text-zinc-300 text-base leading-relaxed mb-6 max-w-md">{cards[0].description}</p>

                {cards[0].features && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {cards[0].features.map((f) => (
                      <span key={f} className="text-xs bg-white/10 border border-white/15 text-white/80 px-3 py-1 rounded-full">
                        {f}
                      </span>
                    ))}
                  </div>
                )}

                <Link
                  to={cards[0].href}
                  className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-semibold group-hover:gap-3 transition-all duration-200 cursor-pointer"
                >
                  Explore Residential Solar
                  <div className="w-5 h-5 flex items-center justify-center bg-amber-500/20 rounded-full">
                    <i className="ri-arrow-right-line text-xs" />
                  </div>
                </Link>
              </div>
            </div>
          )}

          {/* Right column — 2 stacked cards */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            {cards.slice(1).map((card, i) => (
              <div
                key={card.title}
                className={`group relative overflow-hidden rounded-3xl flex-1 cursor-pointer transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ minHeight: '275px', transitionDelay: `${(i + 1) * 120}ms` }}
              >
                <img
                  src={card.image}
                  alt={card.title}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/90 via-zinc-950/40 to-transparent" />

                {card.badge && (
                  <div className="absolute top-5 left-5 bg-amber-500 text-zinc-950 text-xs font-bold px-3 py-1 rounded-full">
                    {card.badge}
                  </div>
                )}

                <div className="absolute bottom-0 left-0 right-0 p-7">
                  {card.keyword && (
                    <span className="text-amber-400/80 text-xs font-semibold tracking-[0.2em] uppercase mb-2 block">
                      {card.keyword}
                    </span>
                  )}
                  <h3 className="font-serif text-2xl text-white font-semibold mb-2">{card.title}</h3>
                  <p className="text-zinc-300 text-sm leading-relaxed mb-4">{card.description}</p>
                  <Link
                    to={card.href}
                    className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-semibold group-hover:gap-3 transition-all duration-200 cursor-pointer"
                  >
                    Learn More
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs" />
                    </div>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
